import { useEffect } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../../state/store";
import {
  selectDatesRange,
  selectMarketIso,
  selectSelectedGroup,
  selectUserMarkets
} from "../../../state/selectors";
import {
  setAppType,
  setBrowser,
  setChartFilters,
  setConnectivity,
  setDatesRange,
  setDevice,
  setEventsRefresh,
  setGroup,
  setIsFiltersChanged,
  setIsFiltersPanelVisible,
  setLocation,
  setLocationOptions,
  setMarket,
  setMarketEventsRefresh,
  setPeriod,
  setPlatform,
  setSelectedAppType,
  setSelectedBrowser,
  setSelectedConnectivity,
  setSelectedDevice,
  setSelectedFromDate,
  setSelectedGroup,
  setSelectedLocation,
  setSelectedMarket,
  setSelectedPeriod,
  setSelectedPlatform,
  setSelectedToDate
} from "../../../state/ducks";

import { appUserRoles, chartNames, dateRangeFilterOptions } from "../../../consts";
import { appUserService, locationsService } from "../../../services";
import {
  arrayUtil,
  dashboardFiltersUtil,
  dateUtil,
  deviceUtil,
  dropdownUtil
} from "../../../utils";
import { useDashboardFilterOptions, useSelectedDashboardFilters } from "../../../hooks";

import FiltersPanel from "./components/FiltersPanel";
import DefaultFilters from "./components/DefaultFilters";

const onUpdateSelectedOptionList = (selectedId, selectedOptions, allOptions) => {
  let newOptions;

  // if we select 'All' option -> deselect all options if all options selected, else -> select all options
  if (selectedId === 0) {
    if (selectedOptions?.length === allOptions?.length) {
      newOptions = [];
    } else {
      newOptions = allOptions;
    }
  } else {
    newOptions = dropdownUtil.updateSelectedOptions(selectedId, selectedOptions, allOptions, true);

    // if updated option list does not include all options -> make sure that 'All' option is not selected
    if (newOptions?.length !== allOptions?.length) {
      newOptions = newOptions.filter((p) => p.id !== 0);
    }
  }

  // if updated option list conatins all options, except 'All' option -> all list of options is selected
  if (newOptions?.length === allOptions?.length - 1) {
    newOptions = allOptions;
  }

  return newOptions;
};

const DashboardFiltersPanel = ({
  isLoading,
  isMarketAdminOnly,
  onUpdateDateRange,
  isFiltersSet,
  onSavingFilters
}) => {
  const userMarkets = selectUserMarkets()(getState());

  const period = useSelector((store) => store.dashboardFilters.period);
  const device = useSelector((store) => store.dashboardFilters.device);
  const market = useSelector((store) => store.dashboardFilters.market);
  const platform = useSelector((store) => store.dashboardFilters.platform);
  const browser = useSelector((store) => store.dashboardFilters.browser);
  const location = useSelector((store) => store.dashboardFilters.location);
  const appType = useSelector((store) => store.dashboardFilters.appType);
  const group = useSelector((store) => store.dashboardFilters.group);
  const connectivity = useSelector((store) => store.dashboardFilters.connectivity);

  const {
    deviceOptions,
    marketOptions,
    locationOptions,
    platformOptions,
    browserOptions,
    appTypesOptions,
    groupsOptions,
    connectivityOptions,
    worldwideMarketOption,
    allStoresOption
  } = useDashboardFilterOptions();

  const {
    selectedDevice,
    selectedLocation,
    selectedMarkets,
    selectedPlatform,
    selectedBrowser,
    selectedAppType,
    selectedGroup,
    selectedConnectivity,
    selectedPeriod,
    fromDate,
    toDate
  } = useSelectedDashboardFilters();

  const handleSavingFilters = () => {
    dispatch(setDevice(selectedDevice));
    dispatch(setDatesRange(fromDate, toDate));
    dispatch(setLocation(selectedLocation.length ? selectedLocation : null));
    dispatch(setMarket(selectedMarkets.length ? selectedMarkets : null));
    dispatch(setPeriod(selectedPeriod));
    dispatch(setPlatform(selectedPlatform));
    dispatch(setBrowser(selectedBrowser));
    dispatch(setAppType(selectedAppType));
    dispatch(setGroup(selectedGroup));
    dispatch(setConnectivity(selectedConnectivity));

    onSavingFilters();
  };

  const locationsState = useSelector((store) => store.configs.locations);

  const onUpdateDevice = (selectedId) => {
    const newDevice = deviceOptions.find((d) => d.id === selectedId);

    if (selectedId !== selectedDevice.id) {
      dispatch(setSelectedDevice(newDevice));
    }
  };

  const onUpdateMarket = (
    selectedId,
    selectedOptions = selectedMarkets,
    allOptions = marketOptions
  ) => {
    const newMarkets = onUpdateSelectedOptionList(selectedId, selectedOptions, allOptions);

    // arrayUtil.arrayEquals to avoid setting empty locations array if user have only 1 market
    if (
      newMarkets.length === 1 &&
      !arrayUtil.arrayEquals(
        newMarkets.map((m) => m.id),
        selectedMarkets.map((m) => m.id)
      )
    ) {
      dispatch(setSelectedLocation([worldwideMarketOption]));
      dispatch(setLocationOptions([]));
    }

    dispatch(setSelectedMarket(newMarkets));
  };

  const onUpdateLocation = (selectedId) => {
    const newLocation = onUpdateSelectedOptionList(selectedId, selectedLocation, locationOptions);
    dispatch(setSelectedLocation(newLocation));
  };

  const onUpdatePlatform = (selectedId) => {
    const newPlatforms = onUpdateSelectedOptionList(selectedId, selectedPlatform, platformOptions);
    dispatch(setSelectedPlatform(newPlatforms));
  };

  const onUpdateBrowser = (selectedId) => {
    const newBrowsers = onUpdateSelectedOptionList(selectedId, selectedBrowser, browserOptions);
    dispatch(setSelectedBrowser(newBrowsers));
  };

  const onUpdateAppType = (selectedId) => {
    const newAppTypes = onUpdateSelectedOptionList(selectedId, selectedAppType, appTypesOptions);

    dispatch(setSelectedAppType(newAppTypes));
  };

  const onUpdateGroup = (
    selectedId,
    selectedOptions = selectedGroup,
    allOptions = groupsOptions
  ) => {
    const newGroup = onUpdateSelectedOptionList(selectedId, selectedOptions, allOptions);
    dispatch(setSelectedGroup(newGroup));
  };

  const onUpdateConnectivity = (selectedId) => {
    const newConnectivity = onUpdateSelectedOptionList(
      selectedId,
      selectedConnectivity,
      connectivityOptions
    );
    dispatch(setSelectedConnectivity(newConnectivity));
  };

  const handleChartFilters = (device) => {
    let filter = null;
    const extendedDeviceNames = false;

    if (device) {
      filter = extendedDeviceNames
        ? deviceUtil.getDeviceTypesByName(device, extendedDeviceNames)
        : [device];
    }

    Object.keys(chartNames).forEach((key) => {
      dispatch(setChartFilters(chartNames[key], filter));
    });
  };

  const setDefaultFilters = () => {
    dispatch(setSelectedDevice(deviceOptions[0]));
    dispatch(
      setSelectedMarket(
        marketOptions?.length <= 1 ? [worldwideMarketOption, ...marketOptions] : marketOptions
      )
    );
    dispatch(
      setSelectedLocation(
        locationOptions?.length <= 1 ? [allStoresOption, ...locationOptions] : locationOptions
      )
    );
    dispatch(setSelectedPlatform(platformOptions));
    dispatch(setSelectedBrowser(browserOptions));
    dispatch(setSelectedPeriod(dateRangeFilterOptions.THIS_WEEK));
    dispatch(setSelectedAppType(appTypesOptions));
    dispatch(setSelectedGroup(groupsOptions));
    dispatch(setSelectedConnectivity(connectivityOptions));

    const { fromDate, toDate } = dateUtil.calculateRange(dateRangeFilterOptions.THIS_WEEK);

    dispatch(setSelectedFromDate(fromDate));
    dispatch(setSelectedToDate(toDate));
  };

  useEffect(() => {
    if (!isFiltersSet) {
      const selectedGroupOptions = selectSelectedGroup()(getState());
      const validatedGroup = group?.filter((g) => g);

      dispatch(setSelectedDevice(selectedDevice ? selectedDevice : device || deviceOptions[0]));
      dispatch(setDevice(selectedDevice ? selectedDevice : device || deviceOptions[0]));

      dispatch(
        setSelectedMarket(
          selectedMarkets
            ? selectedMarkets
            : userMarkets?.length === 1
            ? [worldwideMarketOption, market]
            : [market] || marketOptions
        )
      );
      dispatch(setMarket(selectedMarkets ? selectedMarkets : [market] || marketOptions));

      dispatch(
        setSelectedLocation(selectedLocation ? selectedLocation : location || locationOptions)
      );
      dispatch(setLocation(selectedLocation ? selectedLocation : location || [allStoresOption]));

      dispatch(
        setSelectedPlatform(selectedPlatform ? selectedPlatform : platform || platformOptions)
      );
      dispatch(setPlatform(selectedPlatform ? selectedPlatform : platform || [platformOptions[0]]));

      dispatch(setSelectedBrowser(selectedBrowser ? selectedBrowser : browser || browserOptions));
      dispatch(setBrowser(selectedBrowser ? selectedBrowser : browser || [browserOptions[0]]));

      dispatch(setSelectedAppType(selectedAppType ? selectedAppType : appType || appTypesOptions));
      dispatch(setAppType(selectedAppType ? selectedAppType : appType || [appTypesOptions[0]]));

      dispatch(
        setSelectedGroup(
          selectedGroupOptions
            ? selectedGroupOptions
            : validatedGroup?.length
            ? validatedGroup
            : groupsOptions
        )
      );
      dispatch(
        setGroup(
          selectedGroupOptions?.length
            ? selectedGroup
            : validatedGroup?.length
            ? validatedGroup
            : [groupsOptions[0]]
        )
      );

      dispatch(
        setSelectedConnectivity(
          selectedConnectivity ? selectedConnectivity : connectivity || connectivityOptions
        )
      );
      dispatch(
        setConnectivity(
          selectedConnectivity ? selectedConnectivity : connectivity || [connectivityOptions[0]]
        )
      );

      dispatch(setIsFiltersChanged(false));
    }
  }, []);

  useEffect(() => {
    if (selectedAppType?.length > 1) {
      dispatch(setSelectedMarket(marketOptions));
      dispatch(setMarket(marketOptions));

      dispatch(
        setSelectedLocation(
          locationOptions?.length <= 1 ? [allStoresOption, ...locationOptions] : locationOptions
        )
      );
      dispatch(
        setLocation(
          locationOptions?.length <= 1 ? [allStoresOption, ...locationOptions] : locationOptions
        )
      );
    }
  }, [selectedAppType?.length]);

  useEffect(() => {
    if (selectedMarkets?.length > 1) {
      if (!locationOptions.some((option) => option.id === 0)) {
        // if last fetched locationOptions has only 1 option
        dispatch(setSelectedLocation([allStoresOption, ...locationOptions]));
        dispatch(setLocation([allStoresOption, ...locationOptions]));
      } else {
        dispatch(setSelectedLocation(locationOptions));
        dispatch(setLocation(locationOptions));
      }
    }
  }, [selectedMarkets?.length]);

  useEffect(() => {
    const market = selectMarketIso()(getState());
    const locations = locationsState[market];

    if (
      appUserService.hasRole(appUserRoles.MarketAdmin) &&
      !locationOptions.length &&
      locations?.length
    ) {
      dispatch(setLocationOptions([allStoresOption, ...locations]));
    }

    if (
      selectedMarkets?.length === 1 ||
      (selectedMarkets?.length === 2 && selectedMarkets[0].id === 0)
    ) {
      let locationOptions = [
        allStoresOption,
        ...locationsService.getLocationOptions(selectedMarkets.at(-1).id)
      ];

      if (locationOptions.length <= 2 && locationOptions.some((option) => option.id === 0)) {
        locationOptions = locationOptions.filter((option) => option.id !== 0);
      }

      dispatch(setLocationOptions(locationOptions));

      // if some of the location items or selected location items is not present in locationOptions for current market
      // or location contains exactly 1 element, and it's "all locations"
      if (
        (location?.length > 0 &&
          (!arrayUtil.includesAll(
            locationOptions.map((l) => l.id),
            location.map((l) => l.id)
          ) ||
            !arrayUtil.includesAll(
              locationOptions.map((l) => l.id),
              selectedLocation.map((l) => l.id)
            ))) ||
        (location?.length === 1 && location[0].id === 0)
      ) {
        dispatch(setSelectedLocation(locationOptions));
      }
    }
  }, [selectedMarkets?.length, locationsState]);

  useEffect(() => {
    // set isFiltersChanged to true if some filters changed
    // isFiltersSet to avoid triggering this useEffect on default filters page
    if (
      isFiltersSet &&
      (getIsDateRangeUpdated() ||
        dashboardFiltersUtil.isDeviceChanged(device, selectedDevice) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(market, selectedMarkets) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(location, selectedLocation) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(platform, selectedPlatform) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(browser, selectedBrowser) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(appType, selectedAppType) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(group, selectedGroup) ||
        dashboardFiltersUtil.isMultiselectFieldChanged(connectivity, selectedConnectivity))
    ) {
      dispatch(setIsFiltersChanged(true));
    } else {
      dispatch(setIsFiltersChanged(false));
    }
  }, [
    selectedPeriod,
    fromDate?.toString(),
    toDate?.toString(),
    period,
    device?.id,
    selectedDevice?.id,
    market?.length,
    selectedMarkets?.length,
    location?.length,
    selectedLocation?.length,
    platform?.length,
    selectedPlatform?.length,
    browser?.length,
    selectedBrowser?.length,
    appType?.length,
    selectedAppType?.length,
    group?.length,
    selectedGroup?.length,
    connectivity?.length,
    selectedConnectivity?.length
  ]);

  const onApplyFilters = () => {
    let refreshEvents = false;
    let refreshMarketEvents = false;

    const isDateRangeUpdated = getIsDateRangeUpdated();

    if (isDateRangeUpdated) {
      refreshEvents = true;
      refreshMarketEvents = true;

      if (fromDate && toDate && selectedPeriod) {
        dispatch(setPeriod(selectedPeriod));

        if (selectedPeriod === dateRangeFilterOptions.RANGE) {
          dispatch(setDatesRange(fromDate, toDate));
        }
      }
    }

    if (dashboardFiltersUtil.isDeviceChanged(device, selectedDevice)) {
      dispatch(setDevice(selectedDevice));
      handleChartFilters(selectedDevice.id ? selectedDevice.value : null);

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(market, selectedMarkets)) {
      dispatch(setMarket(selectedMarkets));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(location, selectedLocation)) {
      dispatch(setLocation(selectedLocation));

      refreshEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(platform, selectedPlatform)) {
      dispatch(setPlatform(selectedPlatform));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(browser, selectedBrowser)) {
      dispatch(setBrowser(selectedBrowser));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(appType, selectedAppType)) {
      dispatch(setAppType(selectedAppType));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(group, selectedGroup)) {
      dispatch(setGroup(selectedGroup));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (dashboardFiltersUtil.isMultiselectFieldChanged(connectivity, selectedConnectivity)) {
      dispatch(setConnectivity(selectedConnectivity));

      refreshEvents = true;
      refreshMarketEvents = true;
    }

    if (refreshEvents) {
      dispatch(setEventsRefresh(true));
    }

    if (refreshMarketEvents) {
      dispatch(setMarketEventsRefresh(true));
    }

    dispatch(setIsFiltersPanelVisible(false));
    dispatch(setIsFiltersChanged(true));
  };

  const getIsDateRangeUpdated = () => {
    if (selectedPeriod === period) {
      if (selectedPeriod === dateRangeFilterOptions.RANGE) {
        const range = selectDatesRange()(getState());

        if (
          fromDate?.getTime() === range?.from?.getTime() &&
          toDate?.getTime() === range?.to?.getTime()
        ) {
          return false;
        }
      } else {
        return false;
      }
    }

    return true;
  };

  return isFiltersSet ? (
    <FiltersPanel
      isLoading={isLoading}
      isMarketAdminOnly={isMarketAdminOnly}
      onUpdateDevice={onUpdateDevice}
      onUpdateMarket={onUpdateMarket}
      onUpdateLocation={onUpdateLocation}
      onUpdatePlatform={onUpdatePlatform}
      onUpdateBrowser={onUpdateBrowser}
      onUpdateDateRange={onUpdateDateRange}
      onUpdateAppType={onUpdateAppType}
      onUpdateGroup={onUpdateGroup}
      onUpdateConnectivity={onUpdateConnectivity}
      onApplyFilters={onApplyFilters}
      onResetFilters={setDefaultFilters}
    />
  ) : (
    <DefaultFilters
      isLoading={isLoading}
      isMarketAdminOnly={isMarketAdminOnly}
      onUpdateDevice={onUpdateDevice}
      onUpdateMarket={onUpdateMarket}
      onUpdateLocation={onUpdateLocation}
      onUpdatePlatform={onUpdatePlatform}
      onUpdateBrowser={onUpdateBrowser}
      onUpdateDateRange={onUpdateDateRange}
      onUpdateAppType={onUpdateAppType}
      onUpdateGroup={onUpdateGroup}
      onUpdateConnectivity={onUpdateConnectivity}
      onSavingFilters={handleSavingFilters}
    />
  );
};

DashboardFiltersPanel.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isMarketAdminOnly: PropTypes.bool.isRequired,
  onUpdateDateRange: PropTypes.func.isRequired,
  isFiltersSet: PropTypes.bool,
  onSavingFilters: PropTypes.func.isRequired
};

export default DashboardFiltersPanel;
